import React, { useMemo, useState, useEffect } from 'react';
import { MantineReactTable } from 'mantine-react-table';
import { useTheme } from '../../ThemeContext'; // Adjust the path as necessary
import { EyeIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import Comments from '../Comments/AddComments';
import { MantineProvider, Loader, Center } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../api/services';
import AddAlerts from './AddAlerts';
import { getAllAlerts } from '../../api/api';

export const Example = ({ handlePageUpdate, updatePage, projectId, alertType }) => {
  const navigate = useNavigate();
  const { theme } = useTheme(); // Get the theme from context
  const [pendingAlerts, setPendingAlerts] = useState([]);
  const [completedAlerts, setCompletedAlerts] = useState([]);
  const [loading, setLoading] = useState(true); // State for tracking loading status
  const [editing, setEditing] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [isCommentOpen, setIsCommentOpen] = useState(false); // Initially closed
  const [rowToDelete, setRowToDelete] = useState(null);
  const [data, setData] = useState([]); // State to hold fetched alerts

  // Fetch the alerts on mount
  useEffect(() => {
  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const response = await getAllAlerts();

      // console.log("Fetched alerts:", response?.data);

      const allAlerts = response?.data.data || response || [];

      // Current time for comparison
      const now = new Date();

      // Classify based on dateTime
      const pending = [];
      const sent = [];

      allAlerts.forEach(alert => {
        // Skip if no dateTime or invalid date
        if (!alert.dateTime) {
          // You can decide where to put alerts without deadline
          // Option A: treat as pending (not yet "sent")
          pending.push(alert);
          return;
        }

        const deadlineDate = new Date(alert.dateTime);

        // Check if valid date
        if (isNaN(deadlineDate.getTime())) {
          pending.push(alert); // or sent, your choice
          return;
        }

        if (deadlineDate < now) {
          // Deadline passed → Sent
          sent.push(alert);
        } else {
          // Deadline in future or today → Pending
          pending.push(alert);
        }
      });

      setPendingAlerts(pending);
      setCompletedAlerts(sent);  // assuming "completedAlerts" is used for "Sent"

      // console.log(`Pending: ${pending.length} | Sent: ${sent.length}`);

    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  fetchAlerts();
}, [updatePage]);

  const columns = useMemo(() => [
    {
      accessorKey: 'title',
      header: 'Title',
      Cell: ({ cell }) => cell.getValue() || 'N/A',
    },
    {
      accessorKey: 'description',
      header: 'Description',
      Cell: ({ cell }) => cell.getValue() || 'N/A',
    },
    {
      accessorKey: 'dateTime',
      header: 'Date',
      Cell: ({ row }) => formatDate(row.original.dateTime) || 'N/A',
    },
    {
      accessorKey: 'type',
      header: 'Type',
      Cell: ({ cell }) => cell.getValue() || 'N/A',
    },
    {
      header: 'Actions',
      Cell: ({ row }) => (
        <div className="flex gap-2">
          <ChatBubbleLeftRightIcon
            className="w-5 h-5 text-blue-600 hover:text-red-800 cursor-pointer"
            onClick={() => openComment(row)}
          />
        </div>
      ),
    },
  ], []);

  const openComment = (row) => {
    setRowToDelete(row.original);
    setIsCommentOpen(true); // Open the comment modal when clicked
  };

  const tableProps = useMemo(() => ({
    columns,
    data: pendingAlerts, // Render only Pending issues initially
    enableColumnActions: true,
    enableColumnFilters: true,
    enablePagination: true,
    enableSorting: true,
    enableRowNumbers: true,
    enableGlobalFilter: true,
    mantineTableProps: {
      highlightOnHover: true,
      withBorder: true,
      sx: {
        'thead > tr': {
          backgroundColor: theme === 'dark' ? '#1a202c' : '#f7fafc',
        },
        'thead > tr > th': {
          backgroundColor: theme === 'dark' ? '#2d3748' : '#edf2f7',
          color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
        },
        'tbody > tr': {
          '&:hover': {
            backgroundColor: theme === 'dark' ? '#2d3748' : '#f1f5f9',
          },
        },
        'tbody > tr > td': {
          backgroundColor: theme === 'dark' ? '#2d3748' : '#ffffff',
          color: theme === 'dark' ? '#ffff' : '#333333',
        },
      },
    },
  }), [columns, theme, pendingAlerts]);

  const table = useMemo(() => ({
    columns,
    data: completedAlerts, // Render only Completed issues
    enableColumnActions: true,
    enableColumnFilters: true,
    enablePagination: true,
    enableSorting: true,
    enableRowNumbers: true,
    enableGlobalFilter: true,
    mantineTableProps: {
      highlightOnHover: true,
      withBorder: true,
      sx: {
        'thead > tr': {
          backgroundColor: theme === 'dark' ? '#1a202c' : '#f7fafc',
        },
        'thead > tr > th': {
          backgroundColor: theme === 'dark' ? '#2d3748' : '#edf2f7',
          color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
        },
        'tbody > tr': {
          '&:hover': {
            backgroundColor: theme === 'dark' ? '#2d3748' : '#f1f5f9',
          },
        },
        'tbody > tr > td': {
          backgroundColor: theme === 'dark' ? '#2d3748' : '#ffffff',
          color: theme === 'dark' ? '#ffff' : '#333333',
        },
      },
    },
  }), [columns, theme, completedAlerts]);

  return (
    <MantineProvider
      theme={{
        colorScheme: theme,
        primaryColor: 'blue',
        primaryShade: 7,
      }}
    >
      <div className="space-y-7">
        <hr />
        {loading ? (
          <Center className="h-96">
            <Loader  />
           
          </Center>
        ) : (
          <div className="flex flex-col gap-5">
            <div>
              {/* Pending Alerts Table */}
              <h2 className="text-md ml-5 mb-5 font-bold text-blue-500">Pending Alerts</h2>
              <MantineReactTable {...tableProps} />
            </div>
            <div className={`h-5 ${theme === "dark" ? "bg-slate-900" : "bg-gray-200"} w-full`}></div>
            {/* Sent Alerts Table */}
            <div>
              <h2 className="text-md ml-5 mb-5 font-bold text-green-600">Sent Alerts</h2>
              <MantineReactTable
                {...table} // Render only Sent issues
              />
            </div>
          </div>
        )}

        {/* Conditional Rendering for Add Issue Form */}
        {editing && (
          <AddAlerts
            isEditing={editing}
            setEditing={setEditing}
            userDetails={editingRow}
            handlePageUpdate={handlePageUpdate}
          />
        )}

        {/* Comments Modal */}
        {isCommentOpen && (
          <Comments
            isOpen={isCommentOpen}
            name={"Comments"}
            type={"alert"}
            setIsOpen={setIsCommentOpen}
          />
        )}
      </div>
    </MantineProvider>
  );
};

export default Example;
